/**
 * api/import.js — queries e mutations para importação de backlog externo.
 *
 * Endpoints (Onda 3):
 *   GET    /projects/{id}/import-sources              — listar fontes conectadas
 *   POST   /projects/{id}/import-sources              — conectar nova fonte (connect)
 *   POST   /projects/{id}/import-sources/{srcId}/sync — sincronizar uma fonte
 *   GET    /projects/{id}/imported-items              — listar itens importados
 *     query: provider?, type?, limit?, offset?
 *
 * Provedores reais nesta fase: github_issues, jira.
 * Os demais (azure_devops, monday, trello, asana, clickup, linear) são stubs visuais.
 *
 * Convenções:
 *   - importKeys seguem hierarquia: ['import', projectId?, sub-recurso?]
 *   - Mutations invalidam a query-key pai ao concluir.
 *   - ApiError 403 → ForbiddenBanner; 429 → RateLimitBanner.
 *   - Clean: sem lógica de UI; composição no componente.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from './client';

// ─── Query Keys ───────────────────────────────────────────────────────────────

export const importKeys = {
  all:            ()            => ['import'],
  sources:        (projectId)  => ['import', projectId, 'sources'],
  items:          (projectId)  => ['import', projectId, 'items'],
  itemsFiltered:  (projectId, filters) => ['import', projectId, 'items', filters],
};

// ─── Queries ──────────────────────────────────────────────────────────────────

/**
 * useImportSources — lista fontes de importação conectadas ao projeto.
 * @param {string|null} projectId
 */
export function useImportSources(projectId) {
  const api = useApi();
  return useQuery({
    queryKey: importKeys.sources(projectId),
    queryFn:  () => api.get(`/projects/${projectId}/import-sources`),
    enabled:  Boolean(projectId),
    staleTime: 30_000,
  });
}

/**
 * useImportedItems — lista itens importados do projeto com filtros opcionais.
 *
 * @param {string|null} projectId
 * @param {{ provider?: string, type?: string, limit?: number, offset?: number }} filters
 */
export function useImportedItems(projectId, filters = {}) {
  const api = useApi();

  const params = new URLSearchParams();
  if (filters.provider) params.set('provider', filters.provider);
  if (filters.type)     params.set('type',     filters.type);
  if (filters.limit)    params.set('limit',    String(filters.limit));
  if (filters.offset)   params.set('offset',   String(filters.offset));
  const qs = params.toString();

  return useQuery({
    queryKey: importKeys.itemsFiltered(projectId, filters),
    queryFn:  () => api.get(`/projects/${projectId}/imported-items${qs ? `?${qs}` : ''}`),
    enabled:  Boolean(projectId),
    staleTime: 20_000,
  });
}

// ─── Mutations ────────────────────────────────────────────────────────────────

/**
 * useConnectImportSource — conecta uma nova fonte de importação ao projeto.
 *
 * Body esperado: { provider: string, credentials?: object, config?: object }
 *
 * @param {string} projectId
 */
export function useConnectImportSource(projectId) {
  const api         = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body) => api.post(`/projects/${projectId}/import-sources`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: importKeys.sources(projectId) });
    },
  });
}

/**
 * useSyncImportSource — dispara uma sincronização para uma fonte existente.
 *
 * @param {string} projectId
 * @param {string} sourceId
 */
export function useSyncImportSource(projectId, sourceId) {
  const api         = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      api.post(`/projects/${projectId}/import-hub/${sourceId}/sync`, {}),
    onSuccess: () => {
      // Invalida fontes (status pode ter mudado) e itens (novos itens podem ter chegado)
      queryClient.invalidateQueries({ queryKey: importKeys.sources(projectId) });
      queryClient.invalidateQueries({ queryKey: importKeys.items(projectId) });
    },
  });
}
